import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useHomeownerAuth } from "@/context/HomeownerAuthContext";

const inputCls =
  "mt-2 bg-transparent border-x-0 border-t-0 border-b border-[#C9C6C6]/25 rounded-none h-12 focus-visible:ring-0 focus-visible:border-[#0E93EC] px-0";
const labelCls =
  "text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]";

function Card({ title, sub, children, testid }) {
  return (
    <section className="bg-[#010204] border border-[#C9C6C6]/10 rounded-xl p-6 md:p-8" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-2">
        — {sub}
      </div>
      <h2 className="font-editorial text-3xl font-light tracking-tighter text-[#EEEEEF] leading-tight mb-6">
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function HomeownerProfile() {
  const { user, loading, logout, updateProfile } = useHomeownerAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState({ profile: false, password: false });

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1421] text-[#C9C6C6]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/homeowner/login" replace />;

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy((b) => ({ ...b, profile: true }));
    try {
      await updateProfile({ name: name.trim(), email: email.trim().toLowerCase() });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update profile");
    } finally {
      setBusy((b) => ({ ...b, profile: false }));
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!pw.current || !pw.next) { toast.error("Fill in both fields"); return; }
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setBusy((b) => ({ ...b, password: true }));
    try {
      await updateProfile({ current_password: pw.current, new_password: pw.next });
      toast.success("Password changed");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setBusy((b) => ({ ...b, password: false }));
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF]" data-testid="homeowner-profile-page">
      <header className="border-b border-[#C9C6C6]/8 bg-[#0D1421] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/TPWH_Logo_Transparent.png" alt="The Pros Work Hub" className="h-9 w-auto shrink-0" data-testid="brand-logo-img" />
            <span className="hidden md:inline-block ml-3 text-[11px] uppercase tracking-[0.22em] text-[#C9C6C6] border-l border-[#C9C6C6]/25 pl-3">
              Homeowner · Profile
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/homeowner/dashboard"
              className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C9C6C6] hover:text-[#EEEEEF]"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-3 h-3" /> Dashboard
            </Link>
            <Button onClick={logout} variant="ghost" className="rounded-none text-[#C9C6C6] hover:text-[#EEEEEF] gap-2" data-testid="profile-logout">
              <LogOut className="w-4 h-4" /><span className="hidden md:inline">Log out</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-10 md:py-14">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-4">— Account</div>
          <h1 className="font-editorial text-5xl md:text-6xl font-light tracking-tighter leading-[0.95]">
            Your profile.
          </h1>
          <p className="mt-6 text-[#C9C6C6] max-w-lg">
            Update your name, email, and password. Changes take effect immediately.
          </p>
        </motion.div>

        <div className="space-y-6">
          <Card title="Name & email" sub="Personal info" testid="profile-info-card">
            <form onSubmit={saveProfile} className="space-y-6" data-testid="profile-info-form">
              <div>
                <Label className={labelCls}>Full name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="profile-name" />
              </div>
              <div>
                <Label className={labelCls}>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="profile-email" />
              </div>
              <Button type="submit" disabled={busy.profile}
                      className="group bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-12 pl-6 pr-4 font-medium"
                      data-testid="profile-save">
                <span className="mr-3">{busy.profile ? "Saving…" : "Save changes"}</span>
                <span className="inline-flex items-center justify-center w-8 h-8 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Button>
            </form>
          </Card>

          <Card title="Change password" sub="Security" testid="profile-password-card">
            <form onSubmit={savePassword} className="space-y-6" data-testid="profile-password-form">
              <div>
                <Label className={labelCls}>Current password</Label>
                <Input type="password" value={pw.current}
                       onChange={(e) => setPw({ ...pw, current: e.target.value })}
                       className={inputCls} data-testid="profile-pw-current" />
              </div>
              <div>
                <Label className={labelCls}>New password <span className="normal-case tracking-normal text-[#C9C6C6] font-normal">(min 8 chars)</span></Label>
                <Input type="password" value={pw.next}
                       onChange={(e) => setPw({ ...pw, next: e.target.value })}
                       className={inputCls} data-testid="profile-pw-next" />
              </div>
              <div>
                <Label className={labelCls}>Confirm new password</Label>
                <Input type="password" value={pw.confirm}
                       onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                       className={inputCls} data-testid="profile-pw-confirm" />
              </div>
              <Button type="submit" disabled={busy.password}
                      className="group bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-12 pl-6 pr-4 font-medium"
                      data-testid="profile-pw-save">
                <span className="mr-3">{busy.password ? "Updating…" : "Change password"}</span>
                <span className="inline-flex items-center justify-center w-8 h-8 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
